import * as React from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import Modal, { ModalSize } from '../misc/components/modal';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';
import { SelectableFixedComment } from '../PulmonaryFunctionTests/Shared/UseFixedComments';
import { AnalysisSetTestFixedComment } from 'webportalclient';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { uniqBy } from '../misc/ArrayFunctions';

export type CompareItems = { title: string, items: Array<AnalysisSetTestFixedComment> };

export interface CommentsCompareSelectDialogProps {
    header: string;
    show: boolean;
    editable?: boolean;
    errorMessage?: string;
    items: Array<SelectableFixedComment>;
    compareItems: CompareItems[];
    showFilter?: boolean;
    onChange: (id: string, checked: boolean) => void;
    onClose: () => void;
}

export interface CommentsCompareSelectorProps {
    onOk: () => void;
    editable?: boolean;
    errorMessage?: string;
    showFilter?: boolean;
    items: Array<SelectableFixedComment>;
    compareItems: CompareItems[];
    onChange: (id: string, checked: boolean) => void;
}

export const CommentsCompareSelectDialog = (props: CommentsCompareSelectDialogProps) => {
    const { header, show, onClose } = props;

    return (
        <Modal show={show} header={header} size={props.compareItems.length === 0 ? ModalSize.Normal : ModalSize.Large} closeModalHandler={() => onClose()}>
            <CommentsCompareSelector {...props} onOk={() => onClose()} />
        </Modal>
    );
}

export const CommentsCompareSelector = ({ onOk, items, compareItems, onChange, errorMessage = null, editable, showFilter = false }: CommentsCompareSelectorProps) => {
    const [filterText, setFilterText] = React.useState();

    const onTextFilter = (event) => {
        setFilterText(event.target.value.toLowerCase());
    }

    const filteredItems = items.filter(i => filterText === undefined || i.comment.toLowerCase().includes(filterText));

    const hideFixedComment = (fixedComment: SelectableFixedComment): boolean => {
        return fixedComment.disabled || !fixedComment.selected || !fixedComment.followOnCommentAvailable;
    };

    const renderItems = () => {
        if (filteredItems.length === 0) {
            return (
                <Alert show={true} category={AlertCategoryEnum.Danger}>
                    There are no fixed comments available
                </Alert>
            );
        }

        const compareItemsVisible = compareItems.length > 0 ? filteredItems.filter(i => compareItems.flatMap(x => x.items).map(it => it.fixedCommentId).includes(i.id)) : [];
        const visibleItems = editable === false ? [...filteredItems.filter(i => i.selected), ...compareItemsVisible] : filteredItems;

        const sortedItems = uniqBy([...visibleItems], (a, b) => a.id === b.id)
            .sort((a, b) => a.displayOrder - b.displayOrder);

        if (sortedItems.length === 0) {
            return (
                <Alert show={true} category={AlertCategoryEnum.Danger}>
                    No selected fixed comments available
                </Alert>
            );
        }

        return sortedItems.map(i => {
            const tooltip = i.description ? (
                <Tooltip id="descriptionTooltip" style={{ zIndex: 2500 }}>
                    {i.description}
                </Tooltip>
            ) : <></>;

            return (
                <React.Fragment key={i.id}>
                    <OverlayTrigger placement="top" overlay={tooltip} animation={null}>
                        <tr>
                            <td style={{ width: compareItems.length === 0 ? '100%': '50%' }}>{i.comment}</td>
                            <td>
                                <div className="checkbox">
                                    <label htmlFor={i.id}>
                                        <input id={i.id} type="checkbox" disabled={!editable} checked={i.selected} onChange={(e) => onChange(e.target.id, e.target.checked)} />
                                    </label>
                                </div>
                            </td>
                            {compareItems.map(ci => (
                                <td>
                                    {ci.items.map(it => it.fixedCommentId).includes(i.id) ? <FontAwesomeIcon className="text-success" icon={faCheck} /> : <FontAwesomeIcon className="text-danger" icon={faXmark} />}
                                </td>
                            ))}
                        </tr>
                    </OverlayTrigger>
                </React.Fragment>
            );
        })
    }

    return (
        <>
            <div className="modal-body">
                <Alert show={errorMessage !== null} category={AlertCategoryEnum.Danger}>
                    {errorMessage}
                </Alert>
                {showFilter ? <div className="form-group">
                    <input type="text" className="form-control" placeholder="Filter..." onChange={onTextFilter} />
                </div> : null}
                <table className="table">
                    <thead>
                        <tr>
                            <th></th>
                            <th><b>Current</b></th>
                            {compareItems.map(ci => (
                                <th>{ci.title}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {renderItems()}
                    </tbody>
                </table>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" data-automationid="submit-modal" disabled={errorMessage !== null} onClick={() => onOk()}>OK</button>
            </div>
        </>
    );
}
